import React, { useState, useContext, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";

export default function Topbar() {
  const [open, setOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const { user: firebaseUser, logout: firebaseLogout } = useContext(AuthContext);
  const [localUser, setLocalUser] = useState(null);
  const [localAdmin, setLocalAdmin] = useState(null);
  const dropdownRef = useRef(null);

  // ✅ Load user/admin from localStorage
  useEffect(() => {
    const loadUsers = () => {
      const storedUser = localStorage.getItem("loggedInUser");
      const storedAdmin = localStorage.getItem("loggedInAdmin");
      setLocalUser(storedUser ? JSON.parse(storedUser) : null);
      setLocalAdmin(storedAdmin ? JSON.parse(storedAdmin) : null);
    };
    loadUsers();

    const handleStorageChange = () => loadUsers();

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("user-login-change", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("user-login-change", handleStorageChange);
    };
  }, []);

  // 🚪 Logout (works for both user & admin)
  const handleLogout = () => {
    if (firebaseUser) firebaseLogout();
    localStorage.removeItem("loggedInUser");
    localStorage.removeItem("loggedInAdmin");
    setLocalUser(null);
    setLocalAdmin(null);
    setShowDropdown(false);
    window.location.href = "/";
  };

  const isAdmin = !!localAdmin;
  const isUser = !!localUser;
  const activeUser = firebaseUser || localUser || localAdmin;

  // ✅ Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <style>{`
        .topbar {
          position: fixed;
          top: 0; left: 0; right: 0;
          width: 100vw;
          z-index: 999;
          background: rgba(255,255,255,0.85);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(0,0,0,0.08);
          box-shadow: 0 4px 14px rgba(0,0,0,0.08);
        }
        .topbar-inner {
          max-width: 1300px;
          margin: 0 auto;
          padding: 14px 26px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .logo {
          font-weight: 900;
          font-size: 34px;
          cursor: pointer;
          font-family: 'Poppins', sans-serif;
          color: #0b3038;
          display: flex;
          align-items: center;
        }
        .logo span {
          color: #03b8b3;
          font-family: "Caveat";
          font-size: 40px;
          margin-left: 3px;
          font-weight: 800;
        }
        .nav {
          display: flex;
          align-items: center;
          gap: 22px;
        }
        .link {
          font-size: 18px;
          font-weight: 600;
          color: #022c32;
          transition: 0.25s;
        }
        .link:hover { color: #03b8b3; }
        .login-btn {
          padding: 8px 18px;
          border-radius: 8px;
          background: linear-gradient(120deg, #03b8b3, #04d4cd);
          color: #fff;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: 0.2s ease;
        }
        .login-btn:hover { transform: scale(1.05); }
        .user-avatar {
          width: 40px;
          height: 40px;
          background: linear-gradient(120deg,#03b8b3,#04d4cd);
          color: white;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 50%;
          font-weight: 700;
          cursor: pointer;
          position: relative;
          text-transform: uppercase;
          font-size: 16px;
        }
        .dropdown {
          position: absolute;
          top: 50px;
          right: 0;
          background: #fff;
          border-radius: 14px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
          flex-direction: column;
          width: 180px;
          padding: 12px;
          display: none;
          z-index: 200;
        }
        .dropdown.open {
          display: flex;
          animation: fadeIn 0.25s ease;
        }
        .dropdown a,
        .dropdown button {
          display: block;
          text-align: center;
          padding: 12px;
          font-size: 16px;
          border-radius: 10px;
          background: none;
          border: none;
          font-weight: 600;
          color: #022c32;
          margin-bottom: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .dropdown a:hover {
          background: rgba(3, 184, 179, 0.1);
          color: #03b8b3;
        }
        .dropdown button:last-child {
          background: linear-gradient(120deg, #e94560, #ff6b81);
          color: white;
          font-weight: 700;
          border: none;
        }
        .dropdown button:last-child:hover {
          transform: scale(1.04);
          box-shadow: 0 3px 10px rgba(233, 69, 96, 0.4);
        }
        .hamb {
          display: none;
          background: none;
          border: none;
          font-size: 28px;
          cursor: pointer;
          color: #022c32;
        }
        @media (max-width: 768px) {
          .hamb { display: inline-flex; }
          .nav {
            position: fixed;
            top: 70px;
            right: 16px;
            flex-direction: column;
            background: rgba(255,255,255,0.95);
            border: 1px solid rgba(0,0,0,0.1);
            border-radius: 14px;
            padding: 15px;
            width: min(80vw, 260px);
            display: none;
          }
          .nav.open { display: flex; }
          .user-avatar .dropdown {
            position: relative;
            top: 55px;
            left: 0;
            border: 1px solid #03b8b3;
            border-radius: 16px;
            box-shadow: 0 6px 14px rgba(3, 184, 179, 0.2);
            padding: 14px;
          }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <header className="topbar">
        <div className="topbar-inner">
          <div className="logo" onClick={() => (window.location.href = "/")}>
            Refresh<span>H2O</span>
          </div>

          <button className="hamb" onClick={() => setOpen(!open)}>☰</button>

          <nav className={`nav ${open ? "open" : ""}`}>
            <Link to="/" className="link" onClick={() => setOpen(false)}>
              Home
            </Link>
            <Link to="/bookservices" className="link" onClick={() => setOpen(false)}>
              Book Services
            </Link>
            <Link to="/about" className="link" onClick={() => setOpen(false)}>
              About Us
            </Link>
            <Link to="/contactus" className="link" onClick={() => setOpen(false)}>
              Contact Us
            </Link>

            {/* 🚫 Show Login if no one logged in */}
            {!isUser && !isAdmin && (
              <Link to="/login" onClick={() => setOpen(false)}>
                <button className="login-btn">Login</button>
              </Link>
            )}

            {/* 👤 User Login → Avatar */}
            {isUser && (
              <div
                className="user-avatar"
                ref={dropdownRef}
                onClick={() => setShowDropdown((prev) => !prev)}
              >
                {activeUser?.name?.[0]?.toUpperCase() || "U"}
                <div className={`dropdown ${showDropdown ? "open" : ""}`}>
                  <Link
                    to="/profile"
                    onClick={() => {
                      setShowDropdown(false);
                      setOpen(false);
                    }}
                  >
                    My Account
                  </Link>
                  <button onClick={handleLogout}>Logout</button>
                </div>
              </div>
            )}

            {/* 👑 Admin Login → Avatar with Admin Panel + Logout */}
            {isAdmin && (
              <div
                className="user-avatar"
                ref={dropdownRef}
                onClick={() => setShowDropdown((prev) => !prev)}
              >
                {activeUser?.name?.[0]?.toUpperCase() || "A"}
                <div className={`dropdown ${showDropdown ? "open" : ""}`}>
                  <Link
                    to="/admin"
                    onClick={() => {
                      setShowDropdown(false);
                      setOpen(false);
                    }}
                  >
                    Admin Panel
                  </Link>
                  <button onClick={handleLogout}>Logout</button>
                </div>
              </div>
            )}
          </nav>
        </div>
      </header>
    </>
  );
}
